import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { toast } from 'sonner';
import Header from '../../components/ui/Header';
import BackButton from '../../components/ui/BackButton';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { companyService } from '../../services/companyService';
import { setupService } from '../../services/setupService';
import { siteService } from '../../services/siteService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import useSubscriptionReadOnly from '../../hooks/useSubscriptionReadOnly';
import SubscriptionReadOnlyBanner from '../../components/SubscriptionReadOnlyBanner';


const ROLE_OPTIONS = [
  { value: 'admin', label: 'Admin' },
  { value: 'site_manager', label: 'Site Manager' },
  { value: 'worker', label: 'Worker' },
  { value: 'client', label: 'Client' }
];

const ROLE_BADGE = {
  admin: 'bg-purple-100 text-purple-700',
  site_manager: 'bg-blue-100 text-blue-700',
  worker: 'bg-teal-100 text-teal-700',
  client: 'bg-orange-100 text-orange-700',
};

// Roles that require or allow site selection
const ROLE_SITE_REQUIREMENT = {
  admin: 'none',        // hidden — admins are company-level only
  worker: 'none',       // hidden — workers are company-level first, assigned to sites later
  site_manager: 'none', // hidden — site managers are company-level; assignments added later
  client: 'required',   // must select a site
};

const InviteUsers = () => {
  const { isReadOnly: subscriptionReadOnly } = useSubscriptionReadOnly();
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [sites, setSites] = useState([]);
  const [teamMembers, setTeamMembers] = useState([
    { id: 1, email: '', name: '', role: 'worker', siteId: '' }
  ]);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Invite management state
  const [allUsers, setAllUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [resendingEmail, setResendingEmail] = useState(null);
  const [activeTab, setActiveTab] = useState('invite'); // 'invite' | 'manage'

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [companyResult, sitesResult] = await Promise.all([
        companyService?.getUserCompany(),
        siteService?.getSelectableSites()
      ]);
      if (companyResult?.data) {
        setCompanyId(companyResult?.data?.id);
        loadCompanyUsers(companyResult?.data?.id);
      }
      if (sitesResult?.data?.length > 0) {
        setSites(sitesResult?.data);
      }
    } catch (err) {
      // silently handle load error
    } finally {
      setLoading(false);
    }
  };

  const loadCompanyUsers = useCallback(async (cId) => {
    const id = cId || companyId;
    if (!id) return;
    setUsersLoading(true);
    try {
      const { data, error } = await supabase
        ?.from('user_profiles')
        ?.select('id, email, full_name, role, is_active, created_at')
        ?.eq('company_id', id)
        ?.order('created_at', { ascending: false });
      if (!error) setAllUsers(data || []);
    } catch (err) {
      // silently handle load error
    } finally {
      setUsersLoading(false);
    }
  }, [companyId]);

  const addMember = () => {
    setTeamMembers(prev => [
      ...prev,
      { id: Date.now(), email: '', name: '', role: 'worker', siteId: '' }
    ]);
  };

  const removeMember = (id) => {
    if (teamMembers?.length === 1) return;
    setTeamMembers(prev => prev?.filter(m => m?.id !== id));
    setError('');
  };

  const updateMember = (id, field, value) => {
    setTeamMembers(prev => prev?.map(m => m?.id === id ? { ...m, [field]: value } : m));
    setError('');
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const filledMembers = teamMembers?.filter(m => m?.email?.trim());
    if (filledMembers?.length === 0) {
      setError('Please add at least one email address');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = filledMembers?.filter(m => !emailRegex?.test(m?.email?.trim()));
    if (invalidEmails?.length > 0) {
      setError(`Invalid email: ${invalidEmails?.[0]?.email}`);
      return;
    }

    // Validate: client role must have a site selected
    const clientsWithoutSite = filledMembers?.filter(m => m?.role === 'client' && !m?.siteId);
    if (clientsWithoutSite?.length > 0) {
      setError(`Client invites require a site assignment. Please select a site for: ${clientsWithoutSite?.map(m => m?.email)?.join(', ')}`);
      return;
    }

    setIsSubmitting(true);
    setError('');
    setResults([]);
    try {
      const { data: inviteResults, error: inviteError } = await setupService?.inviteTeamMembersPerRole(
        companyId,
        filledMembers
      );
      if (inviteError) throw inviteError;
      setResults(inviteResults || []);
      const successCount = inviteResults?.filter(r => r?.success === true && r?.type === 'success')?.length;
      const infoCount = inviteResults?.filter(r => r?.success === true && (r?.type === 'info' || r?.type === 'already_on_site'))?.length;
      const failCount = inviteResults?.filter(r => r?.success !== true)?.length;
      if (successCount > 0 || infoCount > 0) {
        const parts = [];
        if (successCount > 0) parts?.push(`${successCount} invite${successCount !== 1 ? 's' : ''} sent`);
        if (infoCount > 0) parts?.push(`${infoCount} existing user${infoCount !== 1 ? 's' : ''} added`);
        setSuccess(`${parts?.join(', ')}${failCount > 0 ? ` (${failCount} failed — see details below)` : ''}`);
        if (companyId) loadCompanyUsers(companyId);
      } else {
        setSuccess('');
      }
      setTeamMembers([{ id: Date.now(), email: '', name: '', role: 'worker', siteId: '' }]);
    } catch (err) {
      console.error('[InviteUsers] invite team failed:', { code: err?.code, statusCode: err?.statusCode, message: err?.message });
      if (err?.code === 'PGRST301' || err?.code === '42501') {
        setError(`Permission denied: ${err?.message ?? 'unknown'}`);
      } else if (err?.statusCode === 429) {
        setError('Too many requests — please wait a moment and try again.');
      } else {
        setError(`Failed to invite team members: ${err?.message ?? 'unknown error'}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendInvite = async (user) => {
    setResendingEmail(user?.email);
    try {
      const { data: inviteResults, error: inviteError } = await setupService?.inviteTeamMembersPerRole(
        companyId,
        [{ email: user?.email, name: user?.full_name || '', role: user?.role, siteId: '' }]
      );
      if (inviteError) throw inviteError;
      const result = inviteResults?.[0];
      if (result?.success) {
        toast?.success(`Invite resent to ${user?.email}`);
      } else {
        toast?.error(result?.message || `Failed to resend invite to ${user?.email}.`);
      }
    } catch (err) {
      console.error('[InviteUsers] resend invite failed:', { code: err?.code, statusCode: err?.statusCode, message: err?.message });
      if (err?.statusCode === 429) {
        toast?.error('Too many requests — please wait a moment before retrying.');
      } else {
        toast?.error(`Failed to resend invite: ${err?.message ?? 'unknown error'}`);
      }
    } finally {
      setResendingEmail(null);
    }
  };

  const handleRemoveUser = async (user) => {
    if (!window.confirm(`Remove ${user?.full_name || user?.email} from your company? This cannot be undone.`)) return;
    setRemovingId(user?.id);
    try {
      await supabase?.from('site_workers')?.delete()?.eq('worker_id', user?.id);
      const { error } = await supabase
        ?.from('user_profiles')
        ?.update({ is_active: false, company_id: null })
        ?.eq('id', user?.id);
      if (error) throw error;
      setAllUsers(prev => prev?.filter(u => u?.id !== user?.id));
      toast?.success(`${user?.full_name || user?.email} removed`);
    } catch (err) {
      console.error('[InviteUsers] remove user failed:', { code: err?.code, message: err?.message });
      if (err?.code === 'PGRST301' || err?.code === '42501') {
        toast?.error(`Permission denied: ${err?.message ?? 'unknown'}`);
      } else {
        toast?.error(`Failed to remove user: ${err?.message ?? 'unknown error'}`);
      }
    } finally {
      setRemovingId(null);
    }
  };

  // Classify users: pending = is_active false or very recently created (no login yet)
  // We treat is_active=false as pending (not yet accepted), is_active=true as accepted
  const pendingUsers = allUsers?.filter(u => u?.is_active === false);
  const acceptedUsers = allUsers?.filter(u => u?.is_active === true && u?.id !== userProfile?.id);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-[calc(100vh-4rem)] pt-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Invite Team - SiteProof</title>
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20 pb-8 px-4 lg:px-6 max-w-3xl mx-auto">
          <div className="mb-6">
            <BackButton fallback="/admin-dashboard" />
            <h1 className="text-2xl font-bold text-foreground mt-4 mb-1">Invite Team Members</h1>
            <p className="text-muted-foreground">Add admins, site managers, workers and clients to your company</p>
          </div>

          <SubscriptionReadOnlyBanner className="mb-6" />

          {/* Tabs */}
          <div className="flex gap-1 bg-muted rounded-lg p-1 mb-6">
            <button
              onClick={() => setActiveTab('invite')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                activeTab === 'invite' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <Icon name="UserPlus" size={15} />
                Send Invites
              </span>
            </button>
            <button
              onClick={() => setActiveTab('manage')}
              className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-all ${
                activeTab === 'manage' ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <span className="flex items-center justify-center gap-2">
                <Icon name="Users" size={15} />
                Manage Team
                {pendingUsers?.length > 0 && (
                  <span className="bg-amber-500 text-white text-xs rounded-full px-1.5 py-0.5 leading-none">
                    {pendingUsers?.length}
                  </span>
                )}
              </span>
            </button>
          </div>

          {/* ── INVITE TAB ── */}
          {activeTab === 'invite' && (
            <>
              {success && (
                <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6 flex items-center gap-3">
                  <Icon name="CheckCircle" size={20} className="text-success" />
                  <p className="text-sm text-success font-medium">{success}</p>
                </div>
              )}

              {/* Role guide */}
              <div className="bg-accent/5 border border-accent/20 rounded-lg p-4 mb-5">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={16} className="text-accent mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p><span className="font-medium text-foreground">Admin / Worker / Site Manager</span> — invited at company level. Site assignments can be added later.</p>
                    <p><span className="font-medium text-foreground">Client</span> — must be assigned to a specific site when invited.</p>
                  </div>
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-6">
                <h2 className="text-lg font-semibold text-foreground mb-4">Team Members</h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {teamMembers?.map((member, index) => {
                    const siteReq = ROLE_SITE_REQUIREMENT?.[member?.role] || 'none';
                    const showSite = siteReq === 'required' || siteReq === 'optional';

                    return (
                      <div key={member?.id} className="border border-border rounded-lg p-4 space-y-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-foreground">Member {index + 1}</span>
                          {teamMembers?.length > 1 && (
                            <button type="button" onClick={() => removeMember(member?.id)} className="text-muted-foreground hover:text-destructive transition-colors">
                              <Icon name="X" size={16} />
                            </button>
                          )}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <Input
                            label="Email address"
                            type="email"
                            value={member?.email}
                            onChange={e => updateMember(member?.id, 'email', e?.target?.value)}
                            placeholder="user@company.com"
                          />
                          <Input
                            label="Full name (optional)"
                            value={member?.name}
                            onChange={e => updateMember(member?.id, 'name', e?.target?.value)}
                            placeholder="John Smith"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-1.5">Role</label>
                          <div className="flex gap-2 flex-wrap">
                            {ROLE_OPTIONS?.map(opt => (
                              <button
                                key={opt?.value}
                                type="button"
                                onClick={() => updateMember(member?.id, 'role', opt?.value)}
                                className={`flex-1 min-w-[80px] py-2 px-3 rounded-md border text-sm font-medium transition-all ${
                                  member?.role === opt?.value
                                    ? 'border-accent bg-accent/10 text-accent' : 'border-border text-muted-foreground hover:border-accent/50'
                                }`}
                              >
                                {opt?.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {/* Site selector — only shown for site_manager (optional) and client (required) */}
                        {showSite && (
                          <div>
                            <label className="block text-sm font-medium text-foreground mb-1.5">
                              Assign to Site
                              {siteReq === 'required' && <span className="text-destructive ml-1">*</span>}
                              {siteReq === 'optional' && <span className="text-muted-foreground ml-1 text-xs">(optional)</span>}
                            </label>
                            {sites?.length === 0 ? (
                              <p className="text-xs text-muted-foreground">
                                No sites yet.{' '}
                                {siteReq === 'required' ? (
                                  <button type="button" onClick={() => navigate('/site-setup-wizard')} className="text-accent hover:underline">Create a site first</button>
                                ) : (
                                  'You can assign a site later.'
                                )}
                              </p>
                            ) : (
                              <select
                                value={member?.siteId}
                                onChange={e => updateMember(member?.id, 'siteId', e?.target?.value)}
                                className="w-full px-3 py-2 text-sm bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent"
                              >
                                <option value="">— {siteReq === 'required' ? 'Select a site' : 'No site (assign later)'} —</option>
                                {sites?.map(site => (
                                  <option key={site?.id} value={site?.id}>{site?.site_name}</option>
                                ))}
                              </select>
                            )}
                          </div>
                        )}

                        {/* Hint for roles with no site selection */}
                        {siteReq === 'none' && (
                          <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                            <Icon name="Building2" size={12} className="text-muted-foreground" />
                            {member?.role === 'admin' ?'Admins are added at company level — no site assignment needed.'
                              : member?.role === 'site_manager' ?'Site Managers are added at company level. Site assignments can be added later.' :'Workers are added at company level. Site assignments can be added later.'}
                          </p>
                        )}
                      </div>
                    );
                  })}

                  <button
                    type="button"
                    onClick={addMember}
                    className="w-full py-3 border-2 border-dashed border-border rounded-lg text-sm text-muted-foreground hover:border-accent hover:text-accent transition-all flex items-center justify-center gap-2"
                  >
                    <Icon name="Plus" size={16} />
                    Add another team member
                  </button>

                  {results?.length > 0 && (
                    <div className="space-y-2">
                      {results?.map((r, i) => {
                        const isError = !r?.success && r?.type !== 'already_on_site';
                        const isInfo = r?.type === 'already_on_site' || r?.type === 'info';

                        return (
                          <div key={i} className={`text-sm p-3 rounded-md ${
                            isError
                              ? 'bg-destructive/10 text-destructive border border-destructive/20'
                              : isInfo
                              ? 'bg-blue-50 text-blue-700 border border-blue-200' : 'bg-success/10 text-success border border-success/20'
                          }`}>
                            <div className="flex items-start gap-2">
                              <Icon name={isError ? 'AlertCircle' : isInfo ? 'Info' : 'CheckCircle'} size={14} className="mt-0.5 flex-shrink-0" />
                              <div className="min-w-0 flex-1">
                                <span className="font-medium">{r?.email}</span>
                                {' — '}
                                <span>{isError ? 'Something went wrong. Please try again.' : (r?.message || (isInfo ? 'User already on this site' : 'Invite sent'))}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {error && (
                    <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
                      <p className="text-sm text-destructive">{error}</p>
                    </div>
                  )}

                  <div className="flex justify-end pt-2">
                    <Button type="submit" loading={isSubmitting} disabled={isSubmitting || subscriptionReadOnly} size="lg">
                      <Icon name="UserPlus" size={18} />
                      Invite Team Members
                    </Button>
                  </div>
                </form>
              </div>

              <div className="mt-4 bg-accent/5 border border-accent/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Icon name="Info" size={18} className="text-accent mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-foreground mb-1">How invitations work</p>
                    <p className="text-sm text-muted-foreground">Invited users receive an email to set up their account. Workers and admins are added to your company and can be assigned to sites later. Share the login URL: <span className="font-mono text-xs bg-muted px-1 py-0.5 rounded">https://site-proof.app/login</span></p>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ── MANAGE TAB ── */}
          {activeTab === 'manage' && (
            <div className="space-y-6">
              {/* Pending Invites */}
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-amber-400" />
                    <h3 className="text-sm font-semibold text-foreground">Pending Invites</h3>
                    <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{pendingUsers?.length}</span>
                  </div>
                </div>
                {usersLoading ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
                ) : pendingUsers?.length === 0 ? (
                  <div className="p-6 text-center">
                    <Icon name="MailCheck" size={32} className="text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No pending invites</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {pendingUsers?.map(user => (
                      <div key={user?.id} className="flex items-center justify-between gap-3 px-5 py-3">
                        <div className="flex items-center justify-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                            <Icon name="Clock" size={14} className="text-amber-600" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{user?.full_name || user?.email}</p>
                            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_BADGE?.[user?.role] || 'bg-muted text-muted-foreground'}`}>
                            {user?.role}
                          </span>
                          <button
                            onClick={() => handleResendInvite(user)}
                            disabled={resendingEmail === user?.email}
                            className="flex items-center gap-1 text-xs text-accent hover:text-accent/80 border border-accent/30 hover:border-accent px-2.5 py-1.5 rounded-md transition-colors disabled:opacity-50"
                            title="Resend invite"
                          >
                            {resendingEmail === user?.email ? (
                              <span className="animate-spin inline-block w-3 h-3 border border-accent border-t-transparent rounded-full" />
                            ) : (
                              <Icon name="RefreshCw" size={12} />
                            )}
                            Resend
                          </button>
                          <button
                            onClick={() => handleRemoveUser(user)}
                            disabled={removingId === user?.id}
                            className="flex items-center gap-1 text-xs text-destructive hover:text-destructive/80 border border-destructive/30 hover:border-destructive px-2.5 py-1.5 rounded-md transition-colors disabled:opacity-50"
                            title="Remove invite"
                          >
                            {removingId === user?.id ? (
                              <span className="animate-spin inline-block w-3 h-3 border border-destructive border-t-transparent rounded-full" />
                            ) : (
                              <Icon name="Trash2" size={12} />
                            )}
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Accepted Users */}
              <div className="bg-card border border-border rounded-lg overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-green-500" />
                    <h3 className="text-sm font-semibold text-foreground">Accepted Users</h3>
                    <span className="text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">{acceptedUsers?.length}</span>
                  </div>
                </div>
                {usersLoading ? (
                  <div className="p-6 text-center text-sm text-muted-foreground">Loading…</div>
                ) : acceptedUsers?.length === 0 ? (
                  <div className="p-6 text-center">
                    <Icon name="Users" size={32} className="text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No accepted users yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-border">
                    {acceptedUsers?.map(user => (
                      <div key={user?.id} className="flex items-center justify-between gap-3 px-5 py-3">
                        <div className="flex items-center justify-center gap-3 min-w-0">
                          <div className="w-8 h-8 rounded-full bg-success/10 flex items-center justify-center flex-shrink-0">
                            <Icon name="UserCheck" size={14} className="text-success" />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{user?.full_name || user?.email}</p>
                            <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_BADGE?.[user?.role] || 'bg-muted text-muted-foreground'}`}>
                            {user?.role}
                          </span>
                          <button
                            onClick={() => handleRemoveUser(user)}
                            disabled={removingId === user?.id}
                            className="flex items-center gap-1 text-xs text-destructive hover:text-destructive/80 border border-destructive/30 hover:border-destructive px-2.5 py-1.5 rounded-md transition-colors disabled:opacity-50"
                            title="Remove user"
                          >
                            {removingId === user?.id ? (
                              <span className="animate-spin inline-block w-3 h-3 border border-destructive border-t-transparent rounded-full" />
                            ) : (
                              <Icon name="Trash2" size={12} />
                            )}
                            Remove
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </main>
      </div>
    </>
  );
};

export default InviteUsers;
